import WidgetWrapper from '../WidgetWrapper';
import { DashboardP1Insights } from '../../types/dashboard.types';

type DashboardFunnelWidgetProps = {
  insights: DashboardP1Insights | null;
  compareInsights?: DashboardP1Insights | null;
  compareLabel?: string;
  loading?: boolean;
};

export default function DashboardFunnelWidget({
  insights,
  compareInsights = null,
  compareLabel = 'Compare',
  loading = false,
}: DashboardFunnelWidgetProps) {
  const steps = insights?.funnel?.steps ?? [];
  const compareSteps = compareInsights?.funnel?.steps ?? [];
  const compareByKey = new Map(compareSteps.map((step) => [step.key, step]));
  const hasCompare = compareSteps.length > 0;

  const signupStep = steps.find((step) => step.key === 'signups');
  const firstDonationStep = steps.find((step) => step.key === 'first_donation');
  const compareSignupStep = compareByKey.get('signups');
  const compareFirstDonationStep = compareByKey.get('first_donation');

  const currentOverall = insights?.funnel?.overallConversionPct ?? 0;
  const compareOverall = compareInsights?.funnel?.overallConversionPct;
  const overallDelta = typeof compareOverall === 'number' ? currentOverall - compareOverall : null;

  const currentSignups = signupStep?.value ?? 0;
  const currentFirstDonations = firstDonationStep?.value ?? 0;
  const compareSignups = compareSignupStep?.value;
  const compareFirstDonations = compareFirstDonationStep?.value;

  const signupsDelta = typeof compareSignups === 'number' ? currentSignups - compareSignups : null;
  const firstDonationsDelta = typeof compareFirstDonations === 'number' ? currentFirstDonations - compareFirstDonations : null;

  const transitionConversion = firstDonationStep?.conversionFromPreviousPct;
  const transitionDropOff = firstDonationStep?.dropOffFromPreviousPct;
  const compareTransitionConversion = compareFirstDonationStep?.conversionFromPreviousPct;
  const compareTransitionDropOff = compareFirstDonationStep?.dropOffFromPreviousPct;

  const transitionConversionDelta =
    typeof transitionConversion === 'number' && typeof compareTransitionConversion === 'number'
      ? transitionConversion - compareTransitionConversion
      : null;

  const transitionDropOffDelta =
    typeof transitionDropOff === 'number' && typeof compareTransitionDropOff === 'number'
      ? transitionDropOff - compareTransitionDropOff
      : null;

  const deltaClass = (delta: number | null) =>
    delta === null
      ? 'text-gray-500 dark:text-gray-400'
      : delta > 0
        ? 'text-emerald-600 dark:text-emerald-300'
        : delta < 0
          ? 'text-rose-600 dark:text-rose-300'
          : 'text-gray-500 dark:text-gray-400';

  const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

  return (
    <WidgetWrapper
      title="Signup to First Donation"
      titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
      className="h-full w-full p-4 md:p-5 bg-gradient-to-br from-cyan-50/80 via-white/75 to-blue-100/75 dark:from-cyan-900/20 dark:via-slate-900/78 dark:to-blue-900/18 border-cyan-200/50 dark:border-cyan-400/15"
    >
      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
      ) : (
        <div className="space-y-3 w-full">
          <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Overall first-donation rate (from signups)</p>
            <div className="mt-2 flex items-end justify-between gap-4">
              <div className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                {currentOverall.toFixed(2)}%
              </div>
              {typeof compareOverall === 'number' && (
                <div className="text-right">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{compareLabel}</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{compareOverall.toFixed(2)}%</p>
                  {overallDelta !== null && (
                    <p className={`text-xs font-semibold ${overallDelta > 0 ? 'text-emerald-600 dark:text-emerald-300' : overallDelta < 0 ? 'text-rose-600 dark:text-rose-300' : 'text-gray-500 dark:text-gray-300'}`}>
                      {overallDelta > 0 ? '+' : ''}{overallDelta.toFixed(2)} pp
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">Journey overview</p>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/70 dark:bg-slate-800/70 px-3 py-3">
                <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Signups</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{currentSignups.toLocaleString()}</p>
                {hasCompare && typeof compareSignups === 'number' && (
                  <p className="text-[11px] mt-1 text-gray-500 dark:text-gray-400">
                    {compareLabel}: {compareSignups.toLocaleString()} •
                    <span className={`ml-1 font-semibold ${deltaClass(signupsDelta)}`}>
                      {signupsDelta && signupsDelta > 0 ? '+' : ''}
                      {signupsDelta?.toLocaleString()}
                    </span>
                  </p>
                )}
              </div>

              <div className="text-center px-1">
                <p className="text-lg leading-none text-gray-500 dark:text-gray-400">→</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Transition</p>
              </div>

              <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/70 dark:bg-slate-800/70 px-3 py-3">
                <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">First donation</p>
                <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">{currentFirstDonations.toLocaleString()}</p>
                {hasCompare && typeof compareFirstDonations === 'number' && (
                  <p className="text-[11px] mt-1 text-gray-500 dark:text-gray-400">
                    {compareLabel}: {compareFirstDonations.toLocaleString()} •
                    <span className={`ml-1 font-semibold ${deltaClass(firstDonationsDelta)}`}>
                      {firstDonationsDelta && firstDonationsDelta > 0 ? '+' : ''}
                      {firstDonationsDelta?.toLocaleString()}
                    </span>
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="rounded-lg border border-white/60 dark:border-white/10 bg-white/70 dark:bg-slate-800/70 px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Step conversion</p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {typeof transitionConversion === 'number' ? `${transitionConversion.toFixed(2)}%` : '—'}
                </p>
                {hasCompare && typeof compareTransitionConversion === 'number' && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {compareLabel}: {compareTransitionConversion.toFixed(2)}% •
                    <span className={`ml-1 font-semibold ${deltaClass(transitionConversionDelta)}`}>
                      {transitionConversionDelta && transitionConversionDelta > 0 ? '+' : ''}
                      {transitionConversionDelta?.toFixed(2)} pp
                    </span>
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-white/60 dark:border-white/10 bg-white/70 dark:bg-slate-800/70 px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Step drop-off</p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {typeof transitionDropOff === 'number' ? `${transitionDropOff.toFixed(2)}%` : '—'}
                </p>
                {hasCompare && typeof compareTransitionDropOff === 'number' && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {compareLabel}: {compareTransitionDropOff.toFixed(2)}% •
                    <span className={`ml-1 font-semibold ${deltaClass(transitionDropOffDelta)}`}>
                      {transitionDropOffDelta && transitionDropOffDelta > 0 ? '+' : ''}
                      {transitionDropOffDelta?.toFixed(2)} pp
                    </span>
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 rounded-lg border border-white/60 dark:border-white/10 bg-white/70 dark:bg-slate-800/70 px-3 py-3">
              <p className="text-[10px] uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">Rate chart</p>
              <div className="mt-2 space-y-2">
                <div>
                  <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                    <span>Current</span>
                    <span>{currentOverall.toFixed(2)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200/70 dark:bg-slate-700/80 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-cyan-500/80 dark:bg-cyan-400/80"
                      style={{ width: `${clampPercent(currentOverall)}%` }}
                    />
                  </div>
                </div>

                {typeof compareOverall === 'number' && (
                  <div>
                    <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-1">
                      <span>{compareLabel}</span>
                      <span>{compareOverall.toFixed(2)}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-200/70 dark:bg-slate-700/80 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-indigo-500/70 dark:bg-indigo-400/70"
                        style={{ width: `${clampPercent(compareOverall)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </WidgetWrapper>
  );
}
